import { useState } from 'react';
import Money from './Money';
import InfoTip from './InfoTip';
import { providerVatLabel, RecoveryProvider } from '../../types/delivery';
import { insuranceBadge } from '../../utils/insuranceDisclaimer';

interface ProviderRegistryTableProps {
  providers: RecoveryProvider[];
  onEdit: (provider: RecoveryProvider) => void;
  /** Soft delete (`hiddenAt`) — the row stays behind every trip it was snapshotted onto. */
  onRemove: (provider: RecoveryProvider) => void;
  /** The provider currently being removed, so its row can show progress and stay disabled. */
  removingId: string | null;
  removeError: string | null;
}

const TYPE_LABEL: Record<string, string> = {
  CONTRACTOR: 'Contractor',
  IN_HOUSE: 'Own fleet',
};

/**
 * Spec 053 Flow F6 — the recovery-provider registry.
 *
 * Providers, not drivers: driver name and phone are typed per assignment, so there is deliberately
 * no driver column and no driver profile anywhere in this screen. Removal is a soft delete —
 * a provider already snapshotted onto a trip must keep existing for that trip (BR-26, AC-41).
 *
 * No Transport column since #1254: the registry is Recovery only, and Zofeur lives on its own card.
 */
export default function ProviderRegistryTable({ providers, onEdit, onRemove, removingId, removeError }: ProviderRegistryTableProps) {
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  if (providers.length === 0) {
    return (
      <div className="admin-empty-state" data-testid="providers-empty">
        <p>
          No recovery providers yet. Add the contractor or own-fleet entry you assign trips to —
          a driver cannot be assigned to a Garagat trip until at least one provider is active.
        </p>
      </div>
    );
  }

  return (
    <>
      {removeError && <div className="admin-info-banner danger" role="alert" data-testid="provider-remove-error">{removeError}</div>}
      <div className="admin-table-wrapper">
        <table className="admin-table" data-testid="providers-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Type</th>
              <th>VAT status</th>
              <th style={{ textAlign: 'right' }}>
                Cost per trip (ex. VAT)
                <InfoTip glossaryKey="providerCostPerTrip" />
              </th>
              <th>Self-billing agreement</th>
              <th>Insurance</th>
              <th>Status</th>
              <th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => {
              const busy = removingId === p.id;
              return (
                <tr key={p.id} data-testid="provider-row">
                  <td>{p.name}</td>
                  <td>{TYPE_LABEL[p.type] ?? p.type}</td>
                  <td>{providerVatLabel(p)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Money amount={p.costPerTrip} glossaryKey="providerCostPerTrip" hideTip />
                    {p.costPerTrip === null && (
                      <div style={{ fontSize: '0.6875rem', color: 'var(--admin-text-muted)' }}>entered at payment</div>
                    )}
                  </td>
                  <td>{p.type === 'IN_HOUSE' ? '—' : p.selfBillingOnFile ? 'On file' : 'Not on file'}</td>
                  {/* Spec 072 — the cover, and whether customers see the disclaimer on this provider's trips. */}
                  <td data-testid="provider-insurance-badge">
                    {insuranceBadge(p.insuredAmount, p.showInsuranceDisclaimer) ?? '—'}
                  </td>
                  <td>
                    <span className={`admin-badge ${p.active ? 'approved' : 'pending'}`}>{p.active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {confirmingId === p.id ? (
                      <>
                        <span style={{ fontSize: '0.75rem', marginRight: '0.5rem' }}>Remove from the registry?</span>
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm admin-btn-danger"
                          disabled={busy}
                          onClick={() => { setConfirmingId(null); onRemove(p); }}
                        >
                          {busy ? 'Removing…' : 'Confirm'}
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm admin-btn-outline"
                          style={{ marginLeft: '0.375rem' }}
                          disabled={busy}
                          onClick={() => setConfirmingId(null)}
                        >
                          Keep
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" disabled={busy} onClick={() => onEdit(p)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm admin-btn-outline"
                          style={{ marginLeft: '0.375rem' }}
                          disabled={busy}
                          onClick={() => setConfirmingId(p.id)}
                          aria-label={`Remove ${p.name}`}
                        >
                          {busy ? 'Removing…' : 'Remove'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
